// src/pages/bookings/BookingForm.jsx
import React, { useState, useEffect } from "react";
import {
    Container,
    Card,
    Form,
    Button,
    Row,
    Col,
    Spinner,
    Alert,
    Badge,
} from "react-bootstrap";
import {
    FaArrowLeft,
    FaSave,
    FaTimes,
    FaBook,
    FaUser,
    FaTruck,
    FaGasPump,
    FaCalendarAlt,
    FaMoneyBillWave,
    FaInfoCircle,
    FaPlug,
} from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    getBookingById,
    createBooking,
    updateBooking,
} from "../../components/services/bookingService";
import { getCustomers } from "../../components/services/customerService";
import { brandApi, cylinderTypeApi } from "../../api/superadmin/masterData.api";
import { getCustomerConnections } from "../../api/tenant/connection.api";
import "../../styles/styles.css";

const BookingForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditing = !!id;

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [customers, setCustomers] = useState([]);
    const [cylinderTypes, setCylinderTypes] = useState([]);
    const [brands, setBrands] = useState([]);
    const [connections, setConnections] = useState([]);
    const [loadingConnections, setLoadingConnections] = useState(false);
    const [loadingDropdowns, setLoadingDropdowns] = useState(true);

    // ✅ Get tenantId from localStorage
    const tenantId = localStorage.getItem("tenantId") || 
                     localStorage.getItem("tenant") || 
                     localStorage.getItem("subdomain") || 
                     "1";

    const [formData, setFormData] = useState({
        customerId: "",
        connectionId: "",
        cylinderTypeId: "",
        brandId: "",
        bookingSource: "walk-in",
        bookingDate: new Date().toISOString().split("T")[0],
        preferredDeliveryDate: new Date(Date.now() + 86400000).toISOString().split("T")[0],
        deliverySlot: "anytime",
        deliveryInstructions: "",
        quantity: 1,
        unitPrice: 0,
        totalAmount: 0,
        advancePaid: 0,
        bookingStatus: "booked",
        notes: "",
    });
    const [validated, setValidated] = useState(false);

    useEffect(() => {
        loadDropdownData();
    }, []);

    useEffect(() => {
        if (isEditing) {
            loadBooking();
        }
    }, [id]);

    // Load connections when customer changes
    useEffect(() => {
        if (formData.customerId) {
            loadCustomerConnections(formData.customerId);
        } else {
            setConnections([]);
        }
    }, [formData.customerId]);

    const loadCustomerConnections = async (customerId) => {
        setLoadingConnections(true);
        try {
            const data = await getCustomerConnections(customerId);
            setConnections(Array.isArray(data) ? data : []);
            console.log("✅ Connections loaded:", data?.length || 0);
        } catch (error) {
            console.error("Failed to load connections:", error);
            setConnections([]);
        } finally {
            setLoadingConnections(false);
        }
    };

    const loadDropdownData = async () => {
        setLoadingDropdowns(true);
        try {
            // ✅ Pass tenantId to brandApi
            const [customersList, cylinderTypesResponse, brandsResponse] = await Promise.all([
                getCustomers({ limit: 1000 }),
                cylinderTypeApi.getAll(),
                brandApi.getAll(tenantId), // ✅ Pass tenantId
            ]);

            // Extract customers data
            let customersData = [];
            if (Array.isArray(customersList)) {
                customersData = customersList;
            } else if (customersList?.data?.data && Array.isArray(customersList.data.data)) {
                customersData = customersList.data.data;
            } else if (customersList?.data && Array.isArray(customersList.data)) {
                customersData = customersList.data;
            }

            // Extract cylinder types data
            let cylinderTypesData = [];
            if (Array.isArray(cylinderTypesResponse)) {
                cylinderTypesData = cylinderTypesResponse;
            } else if (cylinderTypesResponse?.data?.data && Array.isArray(cylinderTypesResponse.data.data)) {
                cylinderTypesData = cylinderTypesResponse.data.data;
            } else if (cylinderTypesResponse?.data && Array.isArray(cylinderTypesResponse.data)) {
                cylinderTypesData = cylinderTypesResponse.data;
            }

            // Extract brands data
            let brandsData = [];
            if (Array.isArray(brandsResponse)) {
                brandsData = brandsResponse;
            } else if (brandsResponse?.data?.data && Array.isArray(brandsResponse.data.data)) {
                brandsData = brandsResponse.data.data;
            } else if (brandsResponse?.data && Array.isArray(brandsResponse.data)) {
                brandsData = brandsResponse.data;
            }

            setCustomers(customersData);
            setCylinderTypes(cylinderTypesData);
            setBrands(brandsData);

            console.log("✅ Customers loaded:", customersData.length);
            console.log("✅ Cylinder types loaded:", cylinderTypesData.length);
            console.log("✅ Brands loaded:", brandsData.length);
            console.log("✅ Tenant ID used:", tenantId);
        } catch (error) {
            console.error("Failed to load dropdown data:", error);
            toast.error("Failed to load dropdown data");
            setCustomers([]);
            setCylinderTypes([]);
            setBrands([]);
        } finally {
            setLoadingDropdowns(false);
        }
    };

    const loadBooking = async () => {
        setLoading(true);
        setError("");
        try {
            const booking = await getBookingById(id);
            setFormData({
                customerId: booking.customer_id || "",
                connectionId: booking.connection_id || "",
                cylinderTypeId: booking.cylinder_type_id || "",
                brandId: booking.brand_id || "",
                bookingSource: booking.booking_source || "walk-in",
                bookingDate: booking.booking_date ? new Date(booking.booking_date).toISOString().split("T")[0] : new Date().toISOString().split("T")[0],
                preferredDeliveryDate: booking.preferred_delivery_date ? new Date(booking.preferred_delivery_date).toISOString().split("T")[0] : "",
                deliverySlot: booking.delivery_slot || "anytime",
                deliveryInstructions: booking.delivery_instructions || "",
                quantity: booking.quantity || 1,
                unitPrice: booking.unit_price || 0,
                totalAmount: booking.total_amount || 0,
                advancePaid: booking.advance_paid || 0,
                bookingStatus: booking.booking_status || "booked",
                notes: booking.notes || "",
            });

            // Load connections for the customer
            if (booking.customer_id) {
                await loadCustomerConnections(booking.customer_id);
            }
        } catch (error) {
            console.error("Failed to load booking:", error);
            setError(error.response?.data?.message || "Failed to load booking");
            toast.error("Failed to load booking");
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setValidated(true);

        if (e.currentTarget.checkValidity() === false) {
            return;
        }

        if (!formData.customerId) {
            toast.error("Please select a customer");
            return;
        }

        setSubmitting(true);
        setError("");

        try {
            const payload = {
                customer_id: parseInt(formData.customerId),
                connection_id: formData.connectionId ? parseInt(formData.connectionId) : null,
                cylinder_type_id: parseInt(formData.cylinderTypeId),
                brand_id: formData.brandId ? parseInt(formData.brandId) : null,
                booking_source: formData.bookingSource,
                booking_date: formData.bookingDate,
                preferred_delivery_date: formData.preferredDeliveryDate,
                delivery_slot: formData.deliverySlot,
                delivery_instructions: formData.deliveryInstructions || null,
                quantity: parseInt(formData.quantity),
                unit_price: parseFloat(formData.unitPrice) || 0,
                total_amount: parseFloat(formData.totalAmount) || 0,
                advance_paid: parseFloat(formData.advancePaid) || 0,
                booking_status: formData.bookingStatus || "booked",
                notes: formData.notes || null,
            };

            console.log("📦 Submitting payload:", payload);

            if (isEditing) {
                await updateBooking(id, payload);
                toast.success("Booking updated successfully!");
            } else {
                await createBooking(payload);
                toast.success("Booking created successfully!");
            }
            navigate("/bookings");
        } catch (error) {
            console.error("Failed to save booking:", error);
            setError(error.response?.data?.message || "Failed to save booking");
            toast.error(error.response?.data?.message || "Failed to save booking");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Container fluid className="p-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
                <ToastContainer position="top-right" autoClose={3000} transition={Bounce} />
                <div className="text-center py-5">
                    <Spinner animation="border" variant="secondary" size="lg" />
                    <h4 className="mt-3">Loading booking...</h4>
                </div>
            </Container>
        );
    }

    return (
        <Container fluid className="px-4 py-3" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
            <ToastContainer position="top-right" autoClose={3000} transition={Bounce} />

            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <Button
                        variant="outline-secondary"
                        onClick={() => navigate("/bookings")}
                        className="mb-2"
                        style={{ borderRadius: "8px", borderColor: "#dee2e6", color: "#495057", fontSize: "14px", padding: "8px 16px" }}
                    >
                        <FaArrowLeft className="me-2" /> Back
                    </Button>
                    <h3 className="fw-bold mb-0" style={{ color: "rgb(30, 58, 111)" }}>
                        {isEditing ? (
                            <>
                                <FaBook className="me-2" style={{ color: "rgb(30, 58, 111)" }} />
                                Edit Booking
                                <Badge bg="secondary" className="ms-2" style={{ fontSize: "11px" }}>#{id}</Badge>
                            </>
                        ) : (
                            <>
                                <FaBook className="me-2" style={{ color: "rgb(30, 58, 111)" }} />
                                New Booking
                            </>
                        )}
                    </h3>
                    <p className="text-muted mt-1" style={{ fontSize: "14px" }}>
                        {isEditing ? "Update the booking details below" : "Fill in the details to create a new booking"}
                    </p>
                </div>
            </div>

            {error && (
                <Alert variant="danger" className="mb-4" onClose={() => setError("")} dismissible>
                    <FaInfoCircle className="me-2" /> {error}
                </Alert>
            )}

            <Card className="border-0 shadow-sm rounded-3">
                <Card.Body className="p-4">
                    <Form noValidate validated={validated} onSubmit={handleSubmit}>
                        {/* Booking Information */}
                        <div className="mb-4">
                            <h6 className="fw-bold mb-3" style={{ color: "rgb(30, 58, 111)", fontSize: "15px" }}>
                                <FaBook className="me-2" /> Booking Information
                            </h6>
                            <Row>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontSize: "14px", fontWeight: "500" }}>
                                            <FaUser className="me-1" style={{ fontSize: "12px", color: "#6c757d" }} />
                                            Customer <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Form.Select
                                            name="customerId"
                                            value={formData.customerId}
                                            onChange={handleChange}
                                            required
                                            className="rounded-2"
                                            style={{ fontSize: "14px", height: "42px" }}
                                            disabled={loadingDropdowns}
                                        >
                                            <option value="">Select Customer</option>
                                            {customers.map((customer) => (
                                                <option key={customer.id} value={customer.id}>
                                                    {customer.name} - {customer.mobile}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontSize: "14px", fontWeight: "500" }}>
                                            <FaPlug className="me-1" style={{ fontSize: "12px", color: "#6c757d" }} />
                                            Connection
                                        </Form.Label>
                                        <Form.Select
                                            name="connectionId"
                                            value={formData.connectionId}
                                            onChange={handleChange}
                                            className="rounded-2"
                                            style={{ fontSize: "14px", height: "42px" }}
                                            disabled={loadingConnections || !formData.customerId}
                                        >
                                            <option value="">Select Connection</option>
                                            {connections.map((conn) => (
                                                <option key={conn.id} value={conn.id}>
                                                    {conn.connection_number || `Connection #${conn.id}`} - {conn.gas_type?.toUpperCase() || 'LPG'}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Text className="text-muted" style={{ fontSize: "12px" }}>
                                            {!formData.customerId ? "Select a customer first" :
                                                loadingConnections ? "Loading connections..." :
                                                    `${connections.length} connections available`}
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontSize: "14px", fontWeight: "500" }}>
                                            <FaCalendarAlt className="me-1" style={{ fontSize: "12px", color: "#6c757d" }} />
                                            Booking Date
                                        </Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="bookingDate"
                                            value={formData.bookingDate}
                                            onChange={handleChange}
                                            className="rounded-2"
                                            style={{ fontSize: "14px", height: "42px" }}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontSize: "14px", fontWeight: "500" }}>
                                            <FaTruck className="me-1" style={{ fontSize: "12px", color: "#6c757d" }} />
                                            Preferred Delivery Date
                                        </Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="preferredDeliveryDate"
                                            value={formData.preferredDeliveryDate}
                                            onChange={handleChange}
                                            className="rounded-2"
                                            style={{ fontSize: "14px", height: "42px" }}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontSize: "14px", fontWeight: "500" }}>
                                            <FaTruck className="me-1" style={{ fontSize: "12px", color: "#6c757d" }} />
                                            Delivery Slot
                                        </Form.Label>
                                        <Form.Select
                                            name="deliverySlot"
                                            value={formData.deliverySlot}
                                            onChange={handleChange}
                                            className="rounded-2"
                                            style={{ fontSize: "14px", height: "42px" }}
                                        >
                                            <option value="anytime">Anytime</option>
                                            <option value="morning">Morning (6AM - 12PM)</option>
                                            <option value="afternoon">Afternoon (12PM - 5PM)</option>
                                            <option value="evening">Evening (5PM - 9PM)</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontSize: "14px", fontWeight: "500" }}>
                                            <FaInfoCircle className="me-1" style={{ fontSize: "12px", color: "#6c757d" }} />
                                            Booking Source
                                        </Form.Label>
                                        <Form.Select
                                            name="bookingSource"
                                            value={formData.bookingSource}
                                            onChange={handleChange}
                                            className="rounded-2"
                                            style={{ fontSize: "14px", height: "42px" }}
                                        >
                                            <option value="walk-in">Walk-in</option>
                                            <option value="phone">Phone</option>
                                            <option value="mobile_app">Mobile App</option>
                                            <option value="whatsapp">WhatsApp</option>
                                            <option value="website">Website</option>
                                            <option value="telegram">Telegram</option>
                                            <option value="other">Other</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </div>

                        {/* Cylinder Details */}
                        <div className="mb-4">
                            <h6 className="fw-bold mb-3" style={{ color: "rgb(30, 58, 111)", fontSize: "15px" }}>
                                <FaGasPump className="me-2" /> Cylinder Details
                            </h6>
                            <Row>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontSize: "14px", fontWeight: "500" }}>
                                            <FaGasPump className="me-1" style={{ fontSize: "12px", color: "#6c757d" }} />
                                            Cylinder Type <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Form.Select
                                            name="cylinderTypeId"
                                            value={formData.cylinderTypeId}
                                            onChange={handleChange}
                                            required
                                            className="rounded-2"
                                            style={{ fontSize: "14px", height: "42px" }}
                                            disabled={loadingDropdowns}
                                        >
                                            <option value="">Select Cylinder Type</option>
                                            {cylinderTypes.map((type) => (
                                                <option key={type.id} value={type.id}>
                                                    {type.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontSize: "14px", fontWeight: "500" }}>
                                            <FaGasPump className="me-1" style={{ fontSize: "12px", color: "#6c757d" }} />
                                            Brand
                                        </Form.Label>
                                        <Form.Select
                                            name="brandId"
                                            value={formData.brandId}
                                            onChange={handleChange}
                                            className="rounded-2"
                                            style={{ fontSize: "14px", height: "42px" }}
                                            disabled={loadingDropdowns}
                                        >
                                            <option value="">Select Brand</option>
                                            {brands.map((brand) => (
                                                <option key={brand.id} value={brand.id}>
                                                    {brand.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontSize: "14px", fontWeight: "500" }}>
                                            <FaGasPump className="me-1" style={{ fontSize: "12px", color: "#6c757d" }} />
                                            Quantity
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="quantity"
                                            value={formData.quantity}
                                            onChange={handleChange}
                                            min="1"
                                            className="rounded-2"
                                            style={{ fontSize: "14px", height: "42px" }}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </div>

                        {/* Payment Details */}
                        <div className="mb-4">
                            <h6 className="fw-bold mb-3" style={{ color: "rgb(30, 58, 111)", fontSize: "15px" }}>
                                <FaMoneyBillWave className="me-2" /> Payment Details
                            </h6>
                            <Row>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontSize: "14px", fontWeight: "500" }}>
                                            <FaMoneyBillWave className="me-1" style={{ fontSize: "12px", color: "#6c757d" }} />
                                            Unit Price (₹)
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="unitPrice"
                                            value={formData.unitPrice}
                                            onChange={handleChange}
                                            step="0.01"
                                            min="0"
                                            className="rounded-2"
                                            style={{ fontSize: "14px", height: "42px" }}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontSize: "14px", fontWeight: "500" }}>
                                            <FaMoneyBillWave className="me-1" style={{ fontSize: "12px", color: "#6c757d" }} />
                                            Total Amount (₹)
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="totalAmount"
                                            value={formData.totalAmount}
                                            onChange={handleChange}
                                            step="0.01"
                                            min="0"
                                            className="rounded-2"
                                            style={{ fontSize: "14px", height: "42px" }}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontSize: "14px", fontWeight: "500" }}>
                                            <FaMoneyBillWave className="me-1" style={{ fontSize: "12px", color: "#6c757d" }} />
                                            Advance Paid (₹)
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="advancePaid"
                                            value={formData.advancePaid}
                                            onChange={handleChange}
                                            step="0.01"
                                            min="0"
                                            className="rounded-2"
                                            style={{ fontSize: "14px", height: "42px" }}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </div>

                        {/* Additional Details */}
                        <div className="mb-4">
                            <h6 className="fw-bold mb-3" style={{ color: "rgb(30, 58, 111)", fontSize: "15px" }}>
                                <FaInfoCircle className="me-2" /> Additional Details
                            </h6>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontSize: "14px", fontWeight: "500" }}>
                                            <FaTruck className="me-1" style={{ fontSize: "12px", color: "#6c757d" }} />
                                            Delivery Instructions
                                        </Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            name="deliveryInstructions"
                                            value={formData.deliveryInstructions}
                                            onChange={handleChange}
                                            placeholder="Special delivery instructions"
                                            className="rounded-2"
                                            style={{ fontSize: "14px" }}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontSize: "14px", fontWeight: "500" }}>
                                            <FaInfoCircle className="me-1" style={{ fontSize: "12px", color: "#6c757d" }} />
                                            Notes
                                        </Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            name="notes"
                                            value={formData.notes}
                                            onChange={handleChange}
                                            placeholder="Additional notes"
                                            className="rounded-2"
                                            style={{ fontSize: "14px" }}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </div>

                        {/* Status Management (Only for Edit) */}
                        {isEditing && (
                            <div className="mb-4">
                                <h6 className="fw-bold mb-3" style={{ color: "rgb(30, 58, 111)", fontSize: "15px" }}>
                                    <FaBook className="me-2" /> Status Management
                                </h6>
                                <Row>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label style={{ fontSize: "14px", fontWeight: "500" }}>
                                                <FaInfoCircle className="me-1" style={{ fontSize: "12px", color: "#6c757d" }} />
                                                Booking Status
                                            </Form.Label>
                                            <Form.Select
                                                name="bookingStatus"
                                                value={formData.bookingStatus}
                                                onChange={handleChange}
                                                className="rounded-2"
                                                style={{ fontSize: "14px", height: "42px" }}
                                            >
                                                <option value="booked">✅ Booked</option>
                                                <option value="assigned">📋 Assigned</option>
                                                <option value="out_for_delivery">🚚 Out for Delivery</option>
                                                <option value="delivered">✅ Delivered</option>
                                                <option value="cancelled">❌ Cancelled</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="d-flex flex-wrap gap-3 mt-4 pt-3 border-top">
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={submitting}
                                style={{
                                    backgroundColor: "rgb(30, 58, 111)",
                                    border: "none",
                                    borderRadius: "8px",
                                    padding: "10px 28px",
                                    fontWeight: "600",
                                    fontSize: "14px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                }}
                            >
                                {submitting ? (
                                    <>
                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                        <span>Saving...</span>
                                    </>
                                ) : (
                                    <>
                                        <FaSave /> {isEditing ? "Update Booking" : "Create Booking"}
                                    </>
                                )}
                            </Button>
                            <Button
                                variant="outline-secondary"
                                onClick={() => navigate("/bookings")}
                                style={{
                                    borderRadius: "8px",
                                    padding: "10px 28px",
                                    borderColor: "#dee2e6",
                                    color: "#495057",
                                    fontWeight: "500",
                                    fontSize: "14px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                }}
                            >
                                <FaTimes /> Cancel
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default BookingForm;